// Shared bits between admin-users.tsx and admin-user-drawer.tsx. Lives in a
// third module so neither parent has to import the other — both import here.
// Avoids the circular ESM dep that would otherwise resolve to undefined at
// first render.

import type { Role, User, UserStatus as UserStatusValue } from '@/api/types';

/** What the initials chip reads: a full `User`, or an audit actor dressed as one. */
export type AvatarUser = Pick<User, 'initials' | 'role' | 'status'>;

export interface AvatarProps {
  user: AvatarUser;
  size?: number;
}

// ── Avatar (initials chip) ─────────────────────────────────────────────
export function Avatar({ user, size = 32 }: AvatarProps) {
  const bg = user.status === 'disabled' ? 'var(--ink-3)'
           : user.status === 'invited' ? 'var(--warn)'
           : user.role === 'admin'    ? 'var(--bad)'
           : user.role === 'reporter' ? 'var(--chart-3)'
           :                            'var(--accent)';
  return (
    <div className="user-avatar" style={{
      width: size, height: size, borderRadius: '50%',
      background: bg, color: 'var(--ink-on-badge)',
      display:'flex', alignItems:'center', justifyContent:'center',
      fontFamily:'var(--font-display)', fontWeight:800,
      fontSize: size * 0.4, letterSpacing:'.02em',
      flexShrink: 0,
    }}>{user.initials}</div>
  );
}

// ── RoleBadge ──────────────────────────────────────────────────────────
export interface RoleBadgeProps {
  role: Role;
}

export function RoleBadge({ role }: RoleBadgeProps) {
  const cfg = role === 'admin'    ? { color:'var(--bad)',     label:'ADMIN' }
            : role === 'reporter' ? { color:'var(--chart-3)', label:'REPORTER' }
            :                       { color:'var(--accent)',  label:'PLANNER' };
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:6,
      fontFamily:'var(--font-mono)', fontSize:10.5, fontWeight:600,
      letterSpacing:'.1em', color:cfg.color,
    }}>
      <span style={{width:7, height:7, borderRadius:'50%', background:cfg.color}}/>
      {cfg.label}
    </span>
  );
}

// ── UserStatus pill ────────────────────────────────────────────────────
export interface UserStatusProps {
  status: UserStatusValue;
}

export function UserStatus({ status }: UserStatusProps) {
  if (status === 'active')   return <span className="status live"><span className="d"/>ACTIVE</span>;
  if (status === 'invited')  return <span className="status draft"><span className="d"/>INVITED</span>;
  if (status === 'disabled') return <span className="status ended"><span className="d"/>DISABLED</span>;
  return <span className="status"><span className="d"/>{status}</span>;
}
